import React, { useRef, useState } from "react";
import { IconButton, Tooltip, Menu, MenuItem, Typography } from "@/components/ui";
import { FaGripHorizontal, FaList, FaThLarge, FaClock, FaThList, FaCode } from "react-icons/fa";
import useTranslation from "@/hooks/use-translation";

export type SearchViewMode = "grid" | "list" | "card" | "timeline" | "masonry" | "embed";

interface SearchViewSelectorProps {
  currentView: SearchViewMode;
  onViewChange: (view: SearchViewMode) => void;
  className?: string;
}

const SearchViewSelector: React.FC<SearchViewSelectorProps> = ({
  currentView,
  onViewChange,
  className = "",
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleViewSelect = (view: SearchViewMode) => {
    onViewChange(view);
    handleClose();
  };

  const viewOptions = [
    {
      value: "grid" as SearchViewMode,
      label: t("grid_view"),
      icon: <FaGripHorizontal className="w-4 h-4" />,
      description: t("grid_view_description"),
    },
    {
      value: "list" as SearchViewMode,
      label: t("list_view"),
      icon: <FaList className="w-4 h-4" />,
      description: t("list_view_description"),
    },
    {
      value: "card" as SearchViewMode,
      label: t("card_view"),
      icon: <FaThLarge className="w-4 h-4" />,
      description: t("card_view_description"),
    },
    {
      value: "timeline" as SearchViewMode,
      label: t("timeline_view"),
      icon: <FaClock className="w-4 h-4" />,
      description: t("timeline_view_description"),
    },
    {
      value: "masonry" as SearchViewMode,
      label: t("masonry_view"),
      icon: <FaThList className="w-4 h-4" />,
      description: t("masonry_view_description"),
    },
    {
      value: "embed" as SearchViewMode,
      label: t("embed_view"),
      icon: <FaCode className="w-4 h-4" />,
      description: t("embed_view_description"),
    },
  ];

  const currentViewOption = viewOptions.find(
    (option) => option.value === currentView
  );

  return (
    <div className={`relative ${className}`}>
      <Tooltip content={t("change_view")}>
        <IconButton
          ref={buttonRef}
          onClick={handleClick}
          aria-controls={open ? "view-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          size="sm"
          variant="ghost"
        >
          {currentViewOption?.icon}
        </IconButton>
      </Tooltip>

      <Menu
        open={open}
        onClose={handleClose}
        anchorEl={buttonRef.current}
      >
        {viewOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleViewSelect(option.value)}
            className={`min-w-[200px] ${currentView === option.value ? "bg-accent-muted" : ""}`}
          >
            <div className="flex items-center gap-3">
              <span className="text-content-secondary">{option.icon}</span>
              <div className="flex flex-col">
                <Typography variant="text-sm" className="font-medium">
                  {option.label}
                </Typography>
                <Typography variant="text-xs" color="secondary">
                  {option.description}
                </Typography>
              </div>
            </div>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

export default SearchViewSelector;
